import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, Image, TextInput, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, spacing } from '../theme';
import { apiService } from '../services/api';
import { RecentPost } from '../screens/dashboard/RecentPostsCard';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';
import { useAppContext } from '../contexts/AppContext';
import { usePermissions } from '../hooks/usePermissions';
import { openNotificationTarget } from '../navigation/notificationRouter';
import { EntityPickerModal } from './EntityPickerModal';
import { firstText } from '../utils/contactIdentifiers';

export interface Comment {
  commentId: string;
  parentCommentId?: string;
  fromName?: string;
  fromPictureUrl?: string;
  message: string;
  createdTime: string;
  likeCount?: number;
  likedByPage?: boolean;
  isReplied?: boolean;
  fromId?: string;
  /** Set once the business answered this comment privately on Messenger. */
  privateRepliedAt?: string;
  /** The commenter's contact, once added or linked by a Messenger reply. */
  contactId?: string;
}

const PRIVATE_REPLY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

interface Reactor {
  id: string;
  name: string;
  type: string;
  pictureUrl?: string;
}

// Facebook's reaction-type → Ionicons + tint mapping, used for the
// stacked-icon summary ("who reacted") — Graph API returns type as an
// upper-case enum. Icon-based, not emoji, so it renders consistently across
// platforms/fonts.
const REACTION_ICON: Record<string, { name: keyof typeof Ionicons.glyphMap; color: string }> = {
  LIKE: { name: 'thumbs-up', color: '#1877F2' },
  LOVE: { name: 'heart', color: '#F33E58' },
  WOW: { name: 'sad-outline', color: '#F7B125' },
  HAHA: { name: 'happy-outline', color: '#F7B125' },
  SAD: { name: 'sad', color: '#F7B125' },
  ANGRY: { name: 'flame', color: '#E9710F' },
  CARE: { name: 'heart-circle', color: '#F7B125' },
};

function reactionIconFor(type: string) {
  return REACTION_ICON[type] || REACTION_ICON.LIKE;
}

function formatCompactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n % 1000 >= 100 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** Facebook-style "who reacted" summary: a small stack of the distinct
 * reaction-type icons present, followed by a tappable named-example +
 * overflow-count link ("Jane, John and 1.2K others reacted to this post")
 * that opens a scrollable list of every visible reactor's name.
 *
 * Graph API's /reactions edge only returns individual reactors the app is
 * permitted to see (typically people who've also interacted with the app in
 * some way) — it is NOT the full reactor list even though the numeric total
 * (totalCount, from reactions.summary(true) / cached post insights) is
 * accurate. When the two disagree, both are shown so the gap reads as a
 * platform limitation rather than a broken count. */
const ReactorsSummary: React.FC<{ reactors: Reactor[]; totalCount: number; onPress: () => void }> = ({ reactors, totalCount, onPress }) => {
  const styles = useThemedStyles(getStyles);
  if (totalCount === 0) return null;

  const distinctTypes = Array.from(new Set(reactors.map((r) => r.type)));
  const namedExamples = reactors.slice(0, 2).map((r) => r.name);
  const unnamedCount = Math.max(totalCount - namedExamples.length, 0);

  let label: string;
  if (namedExamples.length === 0) {
    label = `${formatCompactCount(totalCount)} unknown reacted to this post`;
  } else if (unnamedCount > 0) {
    label = `${namedExamples.join(', ')} and ${formatCompactCount(unnamedCount)} other${unnamedCount === 1 ? '' : 's'} unknown reacted to this post`;
  } else {
    label = `${namedExamples.join(' and ')} reacted to this post`;
  }

  return (
    <View style={styles.reactorsSummaryRow}>
      {distinctTypes.length > 0 && (
        <View style={styles.reactionIconStack}>
          {distinctTypes.slice(0, 3).map((type, i) => {
            const icon = reactionIconFor(type);
            return (
              <View key={type} style={[styles.reactionIconBadge, { backgroundColor: icon.color }, i > 0 && { marginLeft: -6 }]}>
                <Ionicons name={icon.name} size={11} color="#FFFFFF" />
              </View>
            );
          })}
        </View>
      )}
      <TouchableOpacity onPress={onPress} activeOpacity={0.6} style={styles.reactorsSummaryLinkWrap} disabled={reactors.length === 0}>
        <Text style={reactors.length > 0 ? styles.reactorsSummaryLink : styles.reactorsSummaryText} numberOfLines={1}>{label}</Text>
      </TouchableOpacity>
    </View>
  );
};

export interface PostCommentsPanelProps {
  post: RecentPost;
  pageId: string;
  /** Rendered above the tabs, inside the same scroll container as the
   * comments/likers list — lets a caller (e.g. PostsListScreen's right pane)
   * show the post itself as part of one continuous Facebook-style feed
   * instead of a separate bordered card sitting above a distinct panel. */
  headerContent?: React.ReactNode;
}

interface CommentNode extends Comment {
  replies: CommentNode[];
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Builds a parent→children tree from Graph API's flat comment list — a
 * reply is itself a comment with parentCommentId set to the comment it
 * replies to (not always the post), so a flat render otherwise mixes
 * top-level comments and replies together with no visual link between them. */
function buildCommentTree(comments: Comment[]): CommentNode[] {
  const nodeMap = new Map<string, CommentNode>();
  comments.forEach((c) => nodeMap.set(c.commentId, { ...c, replies: [] }));

  const roots: CommentNode[] = [];
  nodeMap.forEach((node) => {
    if (node.parentCommentId && nodeMap.has(node.parentCommentId)) {
      nodeMap.get(node.parentCommentId)!.replies.push(node);
    } else {
      roots.push(node);
    }
  });

  const byTimeAsc = (a: Comment, b: Comment) => new Date(a.createdTime).getTime() - new Date(b.createdTime).getTime();
  nodeMap.forEach((node) => node.replies.sort(byTimeAsc));
  roots.sort((a, b) => byTimeAsc(b, a) as unknown as number).sort((a, b) => new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime());
  return roots;
}

/** Scrollable popover listing every reactor's name + reaction type — opened
 * by tapping the ReactorsSummary sentence, mirroring Facebook's own "who
 * reacted" dialog. */
const ReactorsListPopover: React.FC<{ visible: boolean; reactors: Reactor[]; totalCount: number; loading: boolean; onClose: () => void }> = ({ visible, reactors, totalCount, loading, onClose }) => {
  const styles = useThemedStyles(getStyles);
  const unknownCount = Math.max(totalCount - reactors.length, 0);
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.popoverOverlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={styles.popoverCard} activeOpacity={1} onPress={() => {}}>
          <View style={styles.popoverHeader}>
            <Text style={styles.popoverTitle}>Reactions ({totalCount})</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={20} color={colors.text.primary as string} />
            </TouchableOpacity>
          </View>
          {loading ? (
            <View style={styles.loading}><ActivityIndicator color={colors.primary as string} /></View>
          ) : (
            <ScrollView style={styles.popoverList} contentContainerStyle={styles.popoverListContent}>
              {reactors.map((reactor) => {
                const icon = reactionIconFor(reactor.type);
                return (
                  <View key={reactor.id} style={styles.reactorRow}>
                    {reactor.pictureUrl ? (
                      <Image source={{ uri: reactor.pictureUrl }} style={styles.commentAvatar} />
                    ) : (
                      <View style={[styles.commentAvatar, styles.commentAvatarFallback]}>
                        <Ionicons name="person" size={14} color={colors.text.secondary as string} />
                      </View>
                    )}
                    <Text style={styles.reactorName}>{reactor.name}</Text>
                    <View style={[styles.reactionIconBadge, { backgroundColor: icon.color }]}>
                      <Ionicons name={icon.name} size={11} color="#FFFFFF" />
                    </View>
                  </View>
                );
              })}
              {unknownCount > 0 && (
                <Text style={styles.popoverUnknownNote}>
                  +{formatCompactCount(unknownCount)} more reacted but aren't visible to DISLIX (a Facebook privacy limit on the reactions list, not a data error)
                </Text>
              )}
            </ScrollView>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

/** Comments + reactions ("likers") core UI for a single post — embeddable
 * directly (right pane on wide/web layouts) or wrapped in a Modal sheet
 * (compact/mobile layouts). Comments render as a threaded tree so a reply
 * appears visually nested under the exact comment it replies to, and each
 * comment/reply can itself be liked or replied to. */
export const PostCommentsPanel: React.FC<PostCommentsPanelProps> = ({ post, pageId, headerContent }) => {
  const styles = useThemedStyles(getStyles);
  const [comments, setComments] = useState<Comment[]>([]);
  const [reactors, setReactors] = useState<Reactor[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingReactors, setLoadingReactors] = useState(false);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [likingId, setLikingId] = useState<string | null>(null);
  const [openReplyBox, setOpenReplyBox] = useState<string | null>(null);
  const [showReactorsPopover, setShowReactorsPopover] = useState(false);
  const { t } = useLocale();
  const { navigateTo, showNotification } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  const canComment = hasPermissionAtLeast('fbm.manageComments', 'edit');
  const canAddContacts = hasPermissionAtLeast('contacts.manage', 'edit');
  // "Reply on Messenger" box (one private reply per comment)
  const [messengerFor, setMessengerFor] = useState<string | null>(null);
  const [messengerDraft, setMessengerDraft] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const openContact = (contactId: string) =>
    openNotificationTarget(navigateTo, `contact:${contactId}`, { contactId, channel: 'facebook-messenger' });

  const sendPrivateReply = async (comment: Comment) => {
    const text = messengerDraft.trim();
    if (!text) return;
    setBusyId(comment.commentId);
    try {
      const res: any = await apiService.authenticatedRequest(`/api/facebook-messenger/comments/${encodeURIComponent(comment.commentId)}/private-reply`, {
        method: 'POST',
        body: JSON.stringify({ message: text }),
      });
      if (res.statusCode === 1000) {
        setMessengerFor(null);
        setMessengerDraft('');
        setComments((prev) => prev.map((c) => (c.fromId && c.fromId === comment.fromId
          ? { ...c, contactId: res.data?.contactId || c.contactId, ...(c.commentId === comment.commentId ? { privateRepliedAt: new Date().toISOString(), isReplied: true } : {}) }
          : c)));
        showNotification('success', t('comments.messengerSent'), t('comments.messengerSentHint'));
      } else {
        showNotification('error', t('comments.messengerFailed'), res.errors?.[0]?.message || '');
      }
    } catch (error: any) {
      showNotification('error', t('comments.messengerFailed'), error?.message || '');
    } finally {
      setBusyId(null);
    }
  };

  // "Add contact": a new contact, or add the commenter to an existing one
  const [contactChoiceFor, setContactChoiceFor] = useState<Comment | null>(null);
  const [pickExistingFor, setPickExistingFor] = useState<Comment | null>(null);

  const addAsContact = (comment: Comment) => {
    if (comment.contactId) {
      openContact(comment.contactId);
      return;
    }
    setContactChoiceFor(comment);
  };

  const searchContacts = async ({ search, skip, limit }: { search: string; skip: number; limit: number }) => {
    const res: any = await apiService.getParentContacts({ search: search || undefined, limit, offset: skip });
    const list: any[] = Array.isArray(res?.data) ? res.data : res?.data?.contacts || [];
    return {
      items: list.map((c: any) => ({
        id: String(c._id || c.id),
        label: c.profile?.name || c.name || firstText(c.identifiers?.phone) || t('comments.someone'),
        subtitle: firstText(c.identifiers?.phone) || firstText(c.identifiers?.whatsapp) || firstText(c.identifiers?.email) || undefined,
      })),
      hasMore: list.length === limit,
    };
  };

  const saveCommenterContact = async (comment: Comment, existingContactId?: string) => {
    setBusyId(comment.commentId);
    try {
      const res: any = await apiService.authenticatedRequest(`/api/facebook-messenger/comments/${encodeURIComponent(comment.commentId)}/contact`, {
        method: 'POST',
        body: JSON.stringify(existingContactId ? { contactId: existingContactId } : {}),
      });
      if (res.statusCode === 1000 && res.data?.contactId) {
        const contactId = res.data.contactId;
        setComments((prev) => prev.map((c) => (c.fromId && c.fromId === comment.fromId ? { ...c, contactId } : c)));
        showNotification(
          'success',
          t(existingContactId ? 'comments.addedToContact' : 'comments.contactAdded', { name: comment.fromName || '' }),
          t(res.data?.messengerLinked ? 'comments.messengerLinked' : 'comments.messengerLinksLater'),
        );
      } else {
        showNotification('error', t('comments.contactFailed'), res.errors?.[0]?.message || '');
      }
    } catch (error: any) {
      showNotification('error', t('comments.contactFailed'), error?.message || '');
    } finally {
      setBusyId(null);
    }
  };

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const res = await apiService.getFacebookPostComments(pageId, post.postId);
      setComments(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoadingComments(false);
    }
  }, [post.postId, pageId]);

  const loadReactors = useCallback(async () => {
    setLoadingReactors(true);
    try {
      const res = await apiService.getFacebookPostReactions(pageId, post.postId);
      setReactors(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoadingReactors(false);
    }
  }, [post.postId, pageId]);

  useEffect(() => {
    setOpenReplyBox(null);
    loadComments();
    loadReactors();
  }, [post.postId, loadComments, loadReactors]);

  const tree = useMemo(() => buildCommentTree(comments), [comments]);

  const handleReply = async (parentCommentId: string) => {
    const message = (replyDrafts[parentCommentId] || '').trim();
    if (!message) return;
    setReplyingTo(parentCommentId);
    try {
      const result = await apiService.replyToFacebookComment(parentCommentId, pageId, message);
      if (result.statusCode === 1000) {
        setReplyDrafts((prev) => ({ ...prev, [parentCommentId]: '' }));
        setOpenReplyBox(null);
        await loadComments();
      }
    } finally {
      setReplyingTo(null);
    }
  };

  const toggleLike = async (comment: Comment) => {
    setLikingId(comment.commentId);
    try {
      const result = comment.likedByPage
        ? await apiService.unlikeFacebookComment(comment.commentId, pageId)
        : await apiService.likeFacebookComment(comment.commentId, pageId);
      if (result.statusCode === 1000) {
        setComments((prev) => prev.map((c) =>
          c.commentId === comment.commentId
            ? { ...c, likedByPage: !c.likedByPage, likeCount: (c.likeCount || 0) + (c.likedByPage ? -1 : 1) }
            : c
        ));
      }
    } finally {
      setLikingId(null);
    }
  };

  const renderComment = (comment: CommentNode, depth: number) => (
    <View key={comment.commentId} style={[styles.commentRow, depth > 0 && styles.commentRowNested]}>
      {depth > 0 && <View style={styles.threadLine} />}
      {comment.fromPictureUrl ? (
        <Image source={{ uri: comment.fromPictureUrl }} style={styles.commentAvatar} />
      ) : (
        <View style={[styles.commentAvatar, styles.commentAvatarFallback]}>
          <Ionicons name="person" size={14} color={colors.text.secondary as string} />
        </View>
      )}
      <View style={styles.commentBody}>
        <View style={styles.commentBubble}>
          <Text style={styles.commentFrom}>{comment.fromName || t('comments.someone')}</Text>
          <Text style={styles.commentMessage}>{comment.message}</Text>
        </View>
        <View style={styles.commentMetaRow}>
          <Text style={styles.commentTime}>{timeAgo(comment.createdTime)}</Text>
          <TouchableOpacity onPress={() => toggleLike(comment)} disabled={likingId === comment.commentId}>
            <Text style={[styles.commentAction, comment.likedByPage && styles.commentActionActive]}>
              {t('comments.like')}{(comment.likeCount || 0) > 0 ? ` (${comment.likeCount})` : ''}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setOpenReplyBox(openReplyBox === comment.commentId ? null : comment.commentId)}>
            <Text style={styles.commentAction}>{t('comments.reply')}</Text>
          </TouchableOpacity>
          {/* A customer's comment (not the page's own): answer privately, or keep them as a contact */}
          {comment.fromId !== pageId && canComment && (() => {
            const expired = Date.now() - new Date(comment.createdTime).getTime() > PRIVATE_REPLY_WINDOW_MS;
            const done = !!comment.privateRepliedAt;
            return (
              <TouchableOpacity
                onPress={() => {
                  if (done) return comment.contactId ? openContact(comment.contactId) : undefined;
                  if (expired) return showNotification('info', t('comments.messengerExpired'), t('comments.messengerExpiredHint'));
                  setMessengerDraft('');
                  setMessengerFor(messengerFor === comment.commentId ? null : comment.commentId);
                }}
                style={styles.inlineAction}
              >
                <Ionicons name="logo-facebook" size={11} color={(expired && !done ? colors.text.disabled : '#1877F2') as string} />
                <Text style={[styles.commentAction, expired && !done && styles.commentActionDisabled]}>
                  {t(done ? 'comments.openMessenger' : 'comments.replyOnMessenger')}
                </Text>
              </TouchableOpacity>
            );
          })()}
          {comment.fromId !== pageId && (canAddContacts || comment.contactId) && (
            <TouchableOpacity onPress={() => addAsContact(comment)} disabled={busyId === comment.commentId} style={styles.inlineAction}>
              <Ionicons name={comment.contactId ? 'person' : 'person-add-outline'} size={11} color={colors.primary as string} />
              <Text style={styles.commentAction}>{t(comment.contactId ? 'comments.openContact' : 'comments.addContact')}</Text>
            </TouchableOpacity>
          )}
          {comment.isReplied && (
            <View style={styles.repliedBadge}>
              <Ionicons name="checkmark" size={10} color={colors.success as string} />
              <Text style={styles.repliedText}>{t('comments.replied')}</Text>
            </View>
          )}
        </View>

        {messengerFor === comment.commentId && (
          <View style={[styles.replyRow, styles.messengerRow]}>
            <Ionicons name="logo-facebook" size={16} color="#1877F2" />
            <TextInput
              style={styles.replyInput}
              placeholder={t('comments.messengerPlaceholder', { name: comment.fromName || t('comments.someone') })}
              placeholderTextColor={colors.text.secondary as string}
              value={messengerDraft}
              onChangeText={setMessengerDraft}
              autoFocus
            />
            <TouchableOpacity
              style={styles.replySendButton}
              onPress={() => sendPrivateReply(comment)}
              disabled={busyId === comment.commentId || !messengerDraft.trim()}
            >
              {busyId === comment.commentId ? (
                <ActivityIndicator size="small" color={colors.primary as string} />
              ) : (
                <Ionicons name="send" size={16} color="#1877F2" />
              )}
            </TouchableOpacity>
          </View>
        )}
        {messengerFor === comment.commentId && (
          <Text style={styles.messengerHint}>{t('comments.messengerHint')}</Text>
        )}

        {openReplyBox === comment.commentId && (
          <View style={styles.replyRow}>
            <TextInput
              style={styles.replyInput}
              placeholder={t('comments.replyPlaceholder', { name: comment.fromName || t('comments.someone') })}
              placeholderTextColor={colors.text.secondary as string}
              value={replyDrafts[comment.commentId] || ''}
              onChangeText={(text) => setReplyDrafts((prev) => ({ ...prev, [comment.commentId]: text }))}
              autoFocus
            />
            <TouchableOpacity
              style={styles.replySendButton}
              onPress={() => handleReply(comment.commentId)}
              disabled={replyingTo === comment.commentId || !(replyDrafts[comment.commentId] || '').trim()}
            >
              {replyingTo === comment.commentId ? (
                <ActivityIndicator size="small" color={colors.primary as string} />
              ) : (
                <Ionicons name="send" size={16} color={colors.primary as string} />
              )}
            </TouchableOpacity>
          </View>
        )}

        {comment.replies.length > 0 && (
          <View style={styles.repliesContainer}>
            {comment.replies.map((reply) => renderComment(reply, depth + 1))}
          </View>
        )}
      </View>
    </View>
  );

  const reactionsTotal = post.insights?.reactions ?? reactors.length;

  return (
    <>
    <ScrollView style={styles.container} contentContainerStyle={styles.containerContent}>
      {headerContent}

      <ReactorsSummary reactors={reactors} totalCount={reactionsTotal} onPress={() => setShowReactorsPopover(true)} />
      <ReactorsListPopover
        visible={showReactorsPopover}
        reactors={reactors}
        totalCount={reactionsTotal}
        loading={loadingReactors}
        onClose={() => setShowReactorsPopover(false)}
      />

      <View style={styles.commentsHeader}>
        <Text style={styles.commentsHeaderText}>Comments ({comments.length})</Text>
      </View>

      {loadingComments ? (
        <View style={styles.loading}><ActivityIndicator color={colors.primary as string} /></View>
      ) : tree.length === 0 ? (
        <View style={styles.empty}>
          <Ionicons name="chatbubble-outline" size={24} color={colors.text.secondary as string} />
          <Text style={styles.emptyText}>No comments yet</Text>
        </View>
      ) : (
        <View style={styles.listContent}>
          {tree.map((comment) => renderComment(comment, 0))}
        </View>
      )}
    </ScrollView>
      {/* Add contact: new, or add to an existing contact */}
      <Modal visible={!!contactChoiceFor} transparent animationType="fade" onRequestClose={() => setContactChoiceFor(null)}>
        <TouchableOpacity style={styles.popoverOverlay} activeOpacity={1} onPress={() => setContactChoiceFor(null)}>
          <TouchableOpacity style={styles.popoverCard} activeOpacity={1} onPress={() => {}}>
            <Text style={styles.popoverTitle}>{t('comments.addContactTitle', { name: contactChoiceFor?.fromName || t('comments.someone') })}</Text>
            <TouchableOpacity
              style={styles.choiceRow}
              onPress={() => { const c = contactChoiceFor!; setContactChoiceFor(null); saveCommenterContact(c); }}
            >
              <Ionicons name="person-add-outline" size={18} color={colors.primary as string} />
              <View style={styles.choiceText}>
                <Text style={styles.choiceTitle}>{t('comments.newContact')}</Text>
                <Text style={styles.choiceHint}>{t('comments.newContactHint')}</Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.choiceRow}
              onPress={() => { const c = contactChoiceFor!; setContactChoiceFor(null); setPickExistingFor(c); }}
            >
              <Ionicons name="git-merge-outline" size={18} color={colors.primary as string} />
              <View style={styles.choiceText}>
                <Text style={styles.choiceTitle}>{t('comments.existingContact')}</Text>
                <Text style={styles.choiceHint}>{t('comments.existingContactHint')}</Text>
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
      <EntityPickerModal
        visible={!!pickExistingFor}
        onClose={() => setPickExistingFor(null)}
        title={t('comments.chooseContact')}
        confirmText={t('comments.addHere')}
        fetchItems={searchContacts}
        onConfirm={(ids) => {
          const c = pickExistingFor;
          setPickExistingFor(null);
          if (c && ids[0]) saveCommenterContact(c, ids[0]);
        }}
      />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  choiceRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.md, borderTopWidth: 1, borderTopColor: colors.border },
  choiceText: { flex: 1 },
  choiceTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  choiceHint: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 2 },
  inlineAction: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  commentActionDisabled: { color: colors.text.disabled },
  messengerRow: { borderWidth: 1, borderColor: 'rgba(24,119,242,0.4)', borderRadius: 18, paddingHorizontal: spacing.sm },
  messengerHint: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 4 },
  container: {
    flex: 1,
  },
  containerContent: {
    flexGrow: 1,
  },
  commentsHeader: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  commentsHeaderText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.lg,
  },
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
  },
  listContent: {
    padding: spacing.md,
    gap: spacing.md,
  },
  commentRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  commentRowNested: {
    marginTop: spacing.sm,
    paddingLeft: spacing.sm,
  },
  threadLine: {
    position: 'absolute',
    left: -spacing.xs,
    top: -spacing.sm,
    bottom: 12,
    width: 1,
    backgroundColor: colors.border as string,
  },
  commentAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  commentAvatarFallback: {
    backgroundColor: colors.background.base as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  commentBody: {
    flex: 1,
    minWidth: 0,
  },
  commentBubble: {
    backgroundColor: colors.background.base as string,
    borderRadius: 14,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  commentFrom: {
    fontSize: typography.fontSize.xs,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  commentMessage: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
  },
  commentMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: 4,
    paddingHorizontal: spacing.xs,
  },
  commentTime: {
    fontSize: 11,
    color: colors.text.secondary as string,
  },
  commentAction: {
    fontSize: 11,
    fontWeight: '600' as const,
    color: colors.text.secondary as string,
  },
  commentActionActive: {
    color: colors.primary as string,
  },
  repliedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  repliedText: {
    fontSize: 10,
    color: colors.success as string,
  },
  replyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  replyInput: {
    flex: 1,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: colors.border as string,
    paddingHorizontal: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
  },
  replySendButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  repliesContainer: {
    marginLeft: spacing.xs,
  },
  reactorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  reactorName: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary as string,
  },
  reactorType: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary as string,
    textTransform: 'capitalize',
  },
  reactorsSummaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  reactionIconStack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  reactionIconBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.background.surface as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reactorsSummaryLinkWrap: {
    flex: 1,
  },
  reactorsSummaryLink: {
    fontSize: typography.fontSize.xs,
    color: colors.primary as string,
    textDecorationLine: 'underline',
  },
  reactorsSummaryText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary as string,
  },
  popoverUnknownNote: {
    marginTop: spacing.sm,
    fontSize: 11,
    color: colors.text.secondary as string,
    fontStyle: 'italic',
  },
  popoverOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  popoverCard: {
    width: '90%',
    maxWidth: 360,
    maxHeight: '70%',
    backgroundColor: colors.background.surface as string,
    borderRadius: 14,
    overflow: 'hidden',
  },
  popoverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  popoverTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
  },
  popoverList: {
    flexGrow: 0,
  },
  popoverListContent: {
    padding: spacing.md,
    gap: spacing.md,
  },
});
